import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { blogPosts, BlogPost } from '../data/blogs'

const Blog: React.FC = () => {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null)

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedPost) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [selectedPost])

  const featuredPost = blogPosts.find(post => post.isFeatured) || blogPosts[0]
  // Show only 3 items max including featured
  const displayedPosts = blogPosts.filter(post => post.id !== featuredPost?.id).slice(0, 3)

  return (
    <section className="section_home-blog">
      <div className="padding-global">
        <div className="container-large">
          <div className="py-12 md:py-16">
            <div className="text-align-center margin-bottom margin-xxlarge">
              <div className="max-width-large align-center">
                <div className="margin-bottom margin-small">
                  <h2>Our Latest <span className="text-highlight">Insights</span></h2>
                </div>
                <p className="text-size-medium">Stay informed with our expert analysis and market updates.</p>
              </div>
            </div>


            {featuredPost && (
              <div className="featured-article mb-12">
                <div className="featured-article-card grid grid-cols-1 lg:grid-cols-2 gap-8 items-center bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <div className="featured-image relative h-64 md:h-96 w-full overflow-hidden rounded-xl">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    <span className="featured-badge absolute top-4 left-4 bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-md">Featured</span>
                  </div>
                  <div className="featured-content">
                    <div className="article-meta flex gap-4 text-sm text-gray-500 mb-4">
                      <span className="article-category text-primary-600 font-bold">{featuredPost.category}</span>
                      <span>{featuredPost.date}</span>
                    </div>
                    <h3 className="featured-title text-2xl md:text-3xl font-bold mb-4 font-bricolage">{featuredPost.title}</h3>
                    <p className="featured-excerpt text-gray-600 mb-6 leading-relaxed">{featuredPost.excerpt}</p>
                    <button
                      onClick={() => setSelectedPost(featuredPost)}
                      className="read-more-btn text-primary-600 font-bold hover:text-primary-700 inline-flex items-center"
                    >
                      Read More →
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="blog-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayedPosts.map(post => (
                <article key={post.id} className="blog-card bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col h-full">
                  <div className="blog-image relative h-48 w-full overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className="blog-category-badge absolute top-4 left-4 bg-white/90 backdrop-blur px-2 py-1 rounded text-xs font-bold text-gray-900">{post.category}</div>
                  </div>
                  <div className="blog-content p-6 flex flex-col flex-grow">
                    <div className="blog-meta flex justify-between text-xs text-gray-500 mb-3">
                      <span className="blog-date">🗓️ {post.date}</span>
                      <span className="blog-read-time">⏱️ {post.readTime}</span>
                    </div>
                    <h3 className="blog-title text-xl font-bold mb-3 font-bricolage line-clamp-2">{post.title}</h3>
                    <p className="blog-excerpt text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="mt-auto">
                      <button
                        onClick={() => setSelectedPost(post)}
                        className="w-full py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors"
                      >
                        Read Article
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/blogs"
                className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-full hover:bg-primary-500 hover:text-white transition-all transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
              >
                View All Articles →
              </Link>
            </div>

          </div>
        </div>
      </div>

      {/* Reading Modal */}
      {selectedPost && (
        <div className="reading-modal-overlay" onClick={() => setSelectedPost(null)}>
          <div className="reading-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reading-modal-header">
              <div className="reading-modal-meta">
                <span className="reading-modal-category">{selectedPost.category}</span>
                <span className="reading-modal-date">{selectedPost.date}</span>
                <span className="reading-modal-read-time">{selectedPost.readTime}</span>
              </div>
              <button
                className="reading-modal-close"
                onClick={() => setSelectedPost(null)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="reading-modal-image">
              <img src={selectedPost.image} alt={selectedPost.title} />
            </div>

            <div className="reading-modal-content">
              <h2 className="reading-modal-title">{selectedPost.title}</h2>
              <div className="reading-modal-text">
                {selectedPost.fullContent.split('\n').map((paragraph, index) => (
                  <p key={index} className="reading-paragraph">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default Blog
